import { Component, computed, effect, Inject, Input, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CrmFieldService, StandardIds, SystemFieldIds } from '../../../shared-services/crm-services/field.service';
import { CrmActivityTemplateComponent } from './activity-template.component';
import { CrmInjectionToken, CrmDependencies } from '../../../tokens-and-interfaces';
import { MatCardModule } from '@angular/material/card';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { BlankValueComponent } from '@vendasta/galaxy/table';
import { ActivityInterface } from '@vendasta/crm';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { AVATAR_WIDTH } from '../../../constants';
import { RxState } from '@rx-angular/state';
import { CrmUnknownUserAvatarComponent } from '../../unknown-user-avatar/unknown-user-avatar.component';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { SalespersonService, SalesSdk } from '@vendasta/sales';
import { catchError, map, switchMap, filter } from 'rxjs/operators';
import { combineLatest, firstValueFrom, of, take } from 'rxjs';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { SalesOpportunitiesSdk, SalesOpportunitiesService } from '@vendasta/sales-opportunities';
import { CrmPlatformApiService } from '../../../shared-services/platform-api.service';
import { SanitizeHtmlPipe } from './sanitize-html.pipe';

@Component({
  selector: 'crm-activity-opportunity-updated',
  templateUrl: './opportunity-change.component.html',
  styleUrls: ['./opportunity-change.component.scss'],
  providers: [RxState, CrmPlatformApiService],
  imports: [
    CommonModule,
    TranslateModule,
    MatCardModule,
    CrmActivityTemplateComponent,
    GalaxyPipesModule,
    GalaxyAvatarModule,
    BlankValueComponent,
    CrmUnknownUserAvatarComponent,
    CollapsibleSectionComponent,
    SanitizeHtmlPipe,
  ],
  standalone: true,
})
export class CrmActivityOpportunityUpdatedComponent {
  #activity = signal<ActivityInterface>({});
  isClosedWon = false;
  private checkClosedWon(): void {
    const opportunityChangeStatus =
      this.fieldService.getFieldValueFromCrmObject(this.#activity(), StandardIds.ActivityOpportunityChangePipelineStage)
        ?.stringValue ?? '';

    this.isClosedWon = opportunityChangeStatus.toLowerCase() === 'closed-won';
  }

  ngOnInit(): void {
    this.checkClosedWon();
  }
  @Input() set activity(activity: ActivityInterface) {
    this.#activity.set(activity);
  }

  get activity(): ActivityInterface {
    return this.#activity();
  }

  private _showAvatar = true;
  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;

  @Input() set showAvatar(showAvatar: boolean) {
    this._showAvatar = showAvatar;
  }

  get showAvatar() {
    return this._showAvatar;
  }

  dateFormat = DateFormat.medium;

  salespersonId: Signal<string> = computed(() => {
    return (
      this.fieldService.getFieldValueFromCrmObject(this.activity, StandardIds.ActivityOpportunityChangeSalespersonID)
        ?.stringValue ?? ''
    );
  });

  private readonly namespace = toSignal(this.config.namespace$);

  effectResult = signal<EffectResult | undefined>(undefined);

  headerText = computed(() => {
    const effectResult = this.effectResult();

    const salesPerson = this.salespersonId();
    if (salesPerson) {
      return (
        this.translateService.instant('COMPANY.OPPORTUNITY_CHANGE_ACTIVITY.OPPORTUNITY_UPDATED_BY') +
        ' ' +
        effectResult?.opportunityUpdatedBy
      );
    }
    return this.translateService.instant('COMPANY.OPPORTUNITY_CHANGE_ACTIVITY.OPPORTUNITY_UPDATED');
  });

  cardInfo = computed(() => {
    const headerText = this.headerText();
    const effectiveDate = this.effectiveDate();
    const opportunityName = this.opportunityName();
    const updatedBy = this.effectResult()?.opportunityUpdatedBy;

    const opportunityChangeStatus =
      this.fieldService.getFieldValueFromCrmObject(this.#activity(), StandardIds.ActivityOpportunityChangePipelineStage)
        ?.stringValue ?? '';
    const opportunityChangeLostReason =
      this.fieldService.getFieldValueFromCrmObject(this.#activity(), StandardIds.ActivityOpportunityChangeLostReason)
        ?.stringValue ?? '';
    const opportunityChangeNotes =
      this.getStringFieldData(this.#activity(), StandardIds.ActivityOpportunityChangeNotes) ?? '';

    const formattedStatus =
      opportunityChangeStatus.charAt(0).toUpperCase() + opportunityChangeStatus.slice(1).toLowerCase();
    const formattedLostReason =
      opportunityChangeLostReason.charAt(0).toUpperCase() + opportunityChangeLostReason.slice(1).toLowerCase();

    const activityInfo = {
      status: formattedStatus,
      lostReason: formattedLostReason,
      notes: opportunityChangeNotes,
      opportunityName: opportunityName,
    };

    return {
      headerText,
      effectiveDate,
      updatedBy,
      activityInfo,
    };
  });

  effectiveDate = computed(() => {
    return (
      this.fieldService.getFieldValueFromCrmObject(this.#activity(), SystemFieldIds.ActivityEffectiveDate)?.dateValue ??
      null
    );
  });

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  accountGroupId$ = toObservable(this.#activity).pipe(
    map(
      (activity) =>
        (activity.associations || []).find((association) => association.crmObjectType === 'Company')?.crmObjectId ?? '',
    ),
    filter((companyId) => !!companyId),
    switchMap((companyId) => this.crmPlatformApiService.getAccountGroupForCompany(companyId)),
  );
  protected async handleOpenOpportunity(): Promise<void> {
    const opportunityId = this.getStringFieldData(this.activity, StandardIds.ActivityOpportunityChangeID);
    const accountGroupId = await firstValueFrom(this.accountGroupId$);
    if (this.config.customActions && this.config.customActions.openOpportunitiesDetails) {
      this.config.customActions.openOpportunitiesDetails(opportunityId, accountGroupId);
    }
  }

  opportunityName = toSignal(
    combineLatest([this.accountGroupId$, toObservable(this.#activity)]).pipe(
      switchMap(([accountGroupId, activity]) => {
        const opportunityId = this.getStringFieldData(activity, StandardIds.ActivityOpportunityChangeID);
        return this.opportunityApi.get(opportunityId, accountGroupId).pipe(
          map((opportunity) => opportunity.name),
          catchError(() => of('Unknown')),
        );
      }),
    ),
  );

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
    private readonly crmPlatformApiService: CrmPlatformApiService,
    @Inject(SalespersonService) private readonly salespersonService: SalesSdk,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(SalesOpportunitiesService) private readonly opportunityApi: SalesOpportunitiesSdk,
  ) {
    effect(
      () => {
        const namespace = this.namespace() || '';
        if (this.salespersonId()) {
          return this.salespersonService
            .getSalespersonBySubjectId(namespace, this.salespersonId())
            .pipe(
              map((salesperson) => `${salesperson.firstName} ${salesperson.lastName}`),
              catchError(() => of('Unknown')),
            )
            .pipe(take(1))
            .subscribe((salesperson) => {
              const effectResult = {} as EffectResult;
              effectResult.opportunityUpdatedBy = salesperson;
              this.effectResult.set(effectResult);
            });
        }
      },
      { allowSignalWrites: true },
    );
  }
}

interface EffectResult {
  opportunityUpdatedBy: string;
  effectiveDate: Date | null;
}
